import React, { useMemo } from 'react';
import { Submission, Participant, Task, Project } from '../types';
import { exportSubmissionsToCSV } from '../utils/exportUtils';
import Avatar from './Avatar';
import { useNavigate } from 'react-router-dom';

// ── Helpers ──

const toDate = (value: any): Date => {
  if (!value) return new Date(0);
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
};

const timeAgo = (date: Date): string => {
  const ms = Date.now() - date.getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

// ── SVG Icons ──

const SignalMapIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3L22 20H2Z" />
    <circle cx="8" cy="15" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="14" cy="13" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="11" cy="17" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

// ── Types ──

interface SignificationDashboardProps {
  submissions: Submission[];
  tasks: Task[];
  participants: Participant[];
  participantsMap: Map<string, Participant>;
  currentProject?: Project | null;
}

// ── Component ──

const SignificationDashboard: React.FC<SignificationDashboardProps> = ({
  submissions,
  tasks,
  participants,
  participantsMap,
  currentProject,
}) => {
  const navigate = useNavigate();

  const stats = useMemo(() => {
    const totalExpected = tasks.length * participants.length;
    const completionPct = totalExpected > 0
      ? Math.round((submissions.length / totalExpected) * 100)
      : 0;
    const weakSignals = tasks.filter(t => {
      const assigned = t.assignedParticipantIds?.length || participants.length;
      const done = submissions.filter(s => s.taskId === t.id).length;
      return assigned > 0 && (done / assigned) < 0.3;
    }).length;
    return { completionPct, weakSignals };
  }, [tasks, participants, submissions]);

  const sigTasks = useMemo(() => {
    return tasks.map(task => {
      const config = task.significationConfig;
      const assigned = task.assignedParticipantIds?.length || participants.length;
      const taskSubs = submissions.filter(s => s.taskId === task.id);
      const completed = new Set(taskSubs.map(s => s.participantId)).size;
      const pct = assigned > 0 ? Math.round((completed / assigned) * 100) : 0;

      let modeLabel = 'Classic';
      if (config) {
        const firstItem = config.items?.[0];
        const signType = firstItem?.mode
          ? firstItem.mode.charAt(0).toUpperCase() + firstItem.mode.slice(1)
          : '';
        const collMode = config.collectionMode
          ? config.collectionMode.charAt(0).toUpperCase() + config.collectionMode.slice(1)
          : 'Classic';
        modeLabel = `${signType} · ${collMode}`;
      }

      const typeBadge = config?.items?.[0]?.mode || null;
      const collBadge = config?.collectionMode && config.collectionMode !== 'classic'
        ? config.collectionMode.charAt(0).toUpperCase() + config.collectionMode.slice(1)
        : null;

      let annotation = '';
      if (pct >= 80 && typeBadge === 'triad') annotation = `${Math.ceil(completed / 10)} groups detected`;
      else if (pct >= 60 && typeBadge === 'tension') annotation = 'Two opposing groups';
      else if (pct >= 50 && typeBadge === 'stones') {
        const labels = config?.items?.[0]?.poles?.slice(0, 2).map(p => p.label) || [];
        annotation = labels.length >= 2 ? `${labels[0]} + ${labels[1]} group` : '';
      }

      const journalInfo = config?.collectionMode === 'journal' && config.journalConfig
        ? { current: Math.min(taskSubs.length, config.journalConfig.totalEntries), total: config.journalConfig.totalEntries }
        : null;

      const streakPct = journalInfo && config?.journalConfig
        ? Math.round((taskSubs.length / (participants.length * config.journalConfig.totalEntries)) * 100)
        : null;

      return { task, config, assigned, completed, pct, modeLabel, typeBadge, collBadge, annotation, journalInfo, streakPct };
    });
  }, [tasks, participants, submissions]);

  const recentActivity = useMemo(() => {
    return [...submissions]
      .sort((a, b) => toDate(b.timestamp).getTime() - toDate(a.timestamp).getTime())
      .slice(0, 6)
      .map(sub => {
        const p = participantsMap.get(sub.participantId);
        const task = tasks.find(t => t.id === sub.taskId);
        const sigData = sub.significationData;
        let desc = `completed ${task?.title || task?.prompt || 'a task'}`;
        if (sigData) {
          const responses = (sigData as any)?.responses;
          if (Array.isArray(responses) && responses.length > 0) {
            const r = responses[0];
            if (r.mode === 'triad' && r.weights) {
              const labels = task?.significationConfig?.items?.[0]?.poles?.map(p => p.label) || ['A', 'B', 'C'];
              const vals = r.weights as number[];
              const maxIdx = vals.indexOf(Math.max(...vals));
              desc = `placed near ${labels[maxIdx]} vertex`;
            } else if (r.mode === 'tension' && typeof r.weight === 'number') {
              const pct = Math.round(r.weight * 100);
              const poles = task?.significationConfig?.items?.[0]?.poles;
              const label = pct > 50 ? poles?.[1]?.label : poles?.[0]?.label;
              desc = `shifted toward ${label || 'pole'} ${pct}%`;
            } else if (r.mode === 'stones') {
              desc = 'placed stones';
            }
          }
        }
        return {
          id: sub.id,
          participantName: p?.name || `P-${sub.participantId.slice(-4)}`,
          desc,
          taskName: task?.title || task?.prompt || '',
          time: timeAgo(toDate(sub.timestamp)),
          isSignification: !!sigData,
        };
      });
  }, [submissions, participantsMap, tasks]);

  const topParticipants = useMemo(() => {
    return participants
      .map(p => {
        const assignedTasks = tasks.filter(t =>
          !t.assignedParticipantIds?.length || t.assignedParticipantIds.includes(p.id)
        );
        const completedTasks = assignedTasks.filter(t =>
          submissions.some(s => s.taskId === t.id && s.participantId === p.id)
        );
        const pct = assignedTasks.length > 0
          ? Math.round((completedTasks.length / assignedTasks.length) * 100)
          : 0;
        return { participant: p, pct };
      })
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 5);
  }, [participants, tasks, submissions]);

  const signalPreviewTask = useMemo(() => {
    return sigTasks.find(st => st.typeBadge === 'triad') || sigTasks[0] || null;
  }, [sigTasks]);

  const projectName = currentProject?.name || 'Project';
  const projectClient = currentProject?.client;
  const startDate = currentProject?.startDate
    ? new Date(currentProject.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-text-ink">
            {projectName}{projectClient ? ` — ${projectClient}` : ''}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="w-2 h-2 rounded-full bg-[#6E7A5A]" />
            <span className="text-sm text-text-muted">
              Active{startDate ? ` · Started ${startDate}` : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportSubmissionsToCSV(submissions, tasks, participantsMap)}
            className="px-4 py-2 bg-white border border-app-border text-text-ink text-sm font-semibold rounded-lg hover:bg-app-bg active:scale-95 transition-colors"
          >
            Export data
          </button>
          <button
            onClick={() => navigate('/tasks')}
            className="px-4 py-2 bg-action text-white text-sm font-semibold rounded-lg hover:bg-action/90 active:scale-95 transition-colors"
          >
            + Add task
          </button>
        </div>
      </div>

      {/* Stat Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 bg-white border border-app-border rounded-xl p-4">
        <StatNum label="Participants" value={participants.length} />
        <StatNum label="Tasks" value={tasks.length} />
        <StatNum label="Submissions" value={submissions.length} />
        <StatNum label="Completion" value={`${stats.completionPct}%`} color={stats.completionPct >= 80 ? '#6E7A5A' : undefined} />
        <StatNum label="Worth a look" value={stats.weakSignals} color={stats.weakSignals > 0 ? '#D4715A' : undefined} />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* Left: Signification Tasks */}
        <div>
          <h2 className="text-[11px] font-bold text-text-muted uppercase tracking-[1.5px] mb-3">Signification Tasks</h2>
          <div className="space-y-3">
            {sigTasks.map(({ task, assigned, completed, pct, modeLabel, typeBadge, collBadge, annotation, journalInfo, streakPct }) => (
              <div key={task.id} className="bg-white border border-app-border rounded-xl p-4 flex items-start gap-4 hover:shadow-soft transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-app-bg border border-app-border flex items-center justify-center flex-shrink-0">
                  {typeBadge === 'triad' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted, #6B6560)" strokeWidth="1.5"><path d="M12 3L22 20H2Z" /><circle cx="10" cy="14" r="1.5" fill="var(--text-faint, #A09890)" stroke="none" /><circle cx="14" cy="16" r="1" fill="var(--text-faint, #A09890)" stroke="none" /></svg>
                  )}
                  {typeBadge === 'tension' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted, #6B6560)" strokeWidth="1.5" strokeLinecap="round"><line x1="4" y1="12" x2="20" y2="12" /><circle cx="10" cy="12" r="3" fill="var(--text-faint, #A09890)" fillOpacity="0.4" stroke="none" /><circle cx="15" cy="12" r="2" fill="var(--text-faint, #A09890)" fillOpacity="0.3" stroke="none" /></svg>
                  )}
                  {typeBadge === 'stones' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="7" cy="10" r="3" stroke="var(--text-muted, #6B6560)" strokeWidth="1.5" fill="var(--text-faint, #A09890)" fillOpacity="0.3" /><circle cx="15" cy="8" r="2.5" stroke="var(--text-muted, #6B6560)" strokeWidth="1.5" fill="var(--text-faint, #A09890)" fillOpacity="0.3" /><circle cx="12" cy="16" r="2" stroke="var(--text-muted, #6B6560)" strokeWidth="1.5" fill="var(--text-faint, #A09890)" fillOpacity="0.3" /></svg>
                  )}
                  {!typeBadge && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted, #6B6560)" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 12h6M12 9v6" /></svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[15px] font-bold text-text-ink truncate">{task.title || task.prompt}</h3>
                  <p className="text-xs text-text-muted mt-0.5">
                    {journalInfo ? `Week ${journalInfo.current} of ${journalInfo.total}  ${modeLabel}` : `${completed} / ${assigned} completed  ${modeLabel}`}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {typeBadge && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#6E7A5A]/15 text-[#6E7A5A]">{typeBadge}</span>}
                    {collBadge && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-action/10 text-action">{collBadge}</span>}
                    {annotation && <span className="text-[11px] font-medium text-[#D4715A]">{annotation}</span>}
                    {streakPct !== null && <span className="text-[11px] font-medium text-[#6E7A5A]">{streakPct}% streak</span>}
                  </div>
                  <div className="mt-2.5 h-1 rounded-full bg-app-border overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? '#6E7A5A' : pct > 0 ? '#7BBFAF' : 'transparent' }} />
                  </div>
                </div>
                <button onClick={() => navigate('/signal-map')} className="w-10 h-10 rounded-lg bg-app-bg border border-app-border flex items-center justify-center flex-shrink-0 hover:border-action/40 transition-colors" title="Open Signal Map">
                  <SignalMapIcon className="w-5 h-5 text-text-muted" />
                </button>
              </div>
            ))}
            {sigTasks.length === 0 && (
              <div className="bg-white border border-app-border rounded-xl p-8 text-center">
                <p className="text-sm text-text-muted">No signification tasks yet.</p>
                <button onClick={() => navigate('/tasks')} className="mt-3 text-sm font-semibold text-action hover:underline">Create your first task</button>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="space-y-5">
          {signalPreviewTask && (
            <button onClick={() => navigate('/signal-map')} className="w-full bg-white border border-app-border rounded-xl p-4 text-left hover:shadow-soft hover:border-action/40 transition-all group">
              <h2 className="text-[11px] font-bold text-text-muted uppercase tracking-[1.5px] mb-3">Signal Map Preview</h2>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-app-bg border border-app-border flex items-center justify-center flex-shrink-0"><SignalMapIcon className="w-7 h-7 text-text-muted" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-text-ink truncate">{signalPreviewTask.task.title || signalPreviewTask.task.prompt}</p>
                  <p className="text-xs text-text-muted mt-0.5">{signalPreviewTask.annotation ? `${signalPreviewTask.annotation} · ` : ''}{signalPreviewTask.completed} responses</p>
                </div>
                <ArrowRightIcon className="w-5 h-5 text-text-faint group-hover:text-action transition-colors flex-shrink-0" />
              </div>
            </button>
          )}

          <div className="bg-white border border-app-border rounded-xl p-4">
            <h2 className="text-[11px] font-bold text-text-muted uppercase tracking-[1.5px] mb-3">Recent Activity</h2>
            {recentActivity.length > 0 ? (
              <div className="space-y-0 divide-y divide-app-border">
                {recentActivity.map(item => (
                  <div key={item.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-2">
                      <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${item.isSignification ? 'bg-[#6E7A5A]' : 'bg-text-faint'}`} />
                      <div className="min-w-0">
                        <p className="text-[13px] text-text-ink leading-snug"><span className="font-bold">{item.participantName}</span> {item.desc}{item.taskName ? ` — ${item.taskName}` : ''}</p>
                        <p className="text-[11px] text-text-faint mt-0.5">{item.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-faint py-4 text-center">No activity yet</p>
            )}
          </div>

          <div className="bg-white border border-app-border rounded-xl p-4">
            <h2 className="text-[11px] font-bold text-text-muted uppercase tracking-[1.5px] mb-3">Participants (Top {topParticipants.length})</h2>
            <div className="space-y-0 divide-y divide-app-border">
              {topParticipants.map(({ participant, pct }) => (
                <div key={participant.id} className="py-3 first:pt-0 last:pb-0 flex items-center gap-3">
                  <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className="w-9 h-9 rounded-full flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-text-ink truncate">{participant.name}</p>
                    <p className="text-[11px] text-text-muted truncate">{[participant.occupation, participant.location].filter(Boolean).join(' · ') || 'Participant'}</p>
                  </div>
                  <span className="text-sm font-bold flex-shrink-0" style={{ color: pct >= 100 ? '#6E7A5A' : pct >= 60 ? 'var(--text-ink, #1A1814)' : '#D4715A' }}>{pct}%</span>
                </div>
              ))}
              {topParticipants.length === 0 && <p className="text-sm text-text-faint py-4 text-center">No participants yet</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatNum: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
  <div className="text-center sm:text-left">
    <p className="text-[11px] font-medium text-text-muted uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-extrabold mt-0.5" style={{ color: color || 'var(--text-ink, #1A1814)' }}>{value}</p>
  </div>
);

export default SignificationDashboard;
